import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, MailMinus } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";

// Page publique de désabonnement libre-service (lien au bas de nos courriels).
// Couvre en un seul geste : infolettre (avec ou sans compte) ET prospection B2B.
export default function Unsubscribe() {
  const { lang } = useLang();
  const fr = lang !== "en";
  const [email, setEmail] = useState("");
  const [state, setState] = useState("form"); // form | sending | ok | error

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("sending");
    try {
      await api.post("/unsubscribe", { email: email.trim() });
      setState("ok");
    } catch {
      setState("error");
    }
  };

  return (
    <div data-testid="unsubscribe-page" className="min-h-[70vh] flex items-center justify-center px-6 pt-24 pb-16 spotlight">
      <SEO
        title={fr ? "Désabonnement | Mr Trophée" : "Unsubscribe | Mr Trophée"}
        description={fr ? "Retirez votre adresse de nos listes d'envoi." : "Remove your address from our mailing lists."}
        path="/desinscription" noindex />
      <FadeUp className="w-full max-w-md">
        <div className="bg-surface border border-gold/15 rounded-2xl p-10 text-center">
          <MailMinus className="w-10 h-10 text-gold mx-auto mb-6" />
          <h1 className="font-serif text-3xl mb-3">{fr ? "Désabonnement" : "Unsubscribe"}</h1>

          {state === "ok" ? (
            <>
              <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
              <p className="text-ivory/80 mb-2" data-testid="unsubscribe-success">
                {fr
                  ? "C'est fait — votre adresse est retirée de nos listes (infolettre et communications aux organisations)."
                  : "Done — your address has been removed from our lists (newsletter and organization outreach)."}
              </p>
              <p className="text-ivory/50 text-sm mb-6">
                {fr
                  ? "Les confirmations de commande déjà en cours ne sont pas touchées."
                  : "Order confirmations already in progress are unaffected."}
              </p>
              <Link to="/" data-testid="unsubscribe-home"
                className="inline-block bg-gold text-ink font-bold px-8 py-3 rounded-full text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-all duration-300">
                {fr ? "Retour à l'accueil" : "Back to home"}
              </Link>
            </>
          ) : (
            <form onSubmit={submit}>
              <p className="text-ivory/70 text-sm mb-6">
                {fr
                  ? "Entrez le courriel à retirer de nos listes d'envoi (infolettre et communications aux organisations sportives)."
                  : "Enter the email address to remove from our mailing lists (newsletter and sports organization outreach)."}
              </p>
              <input
                data-testid="unsubscribe-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={fr ? "votre@courriel.ca" : "your@email.ca"}
                className="w-full bg-ink border border-ivory/15 rounded-full px-5 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none transition-colors mb-4"
              />
              {state === "error" && (
                <p className="text-red-400 text-xs mb-4" data-testid="unsubscribe-error">
                  {fr ? "Une erreur est survenue — réessayez." : "Something went wrong — please try again."}
                </p>
              )}
              <button
                data-testid="unsubscribe-submit"
                type="submit"
                disabled={state === "sending"}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-8 py-3 rounded-full text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50"
              >
                {state === "sending" && <Loader2 size={16} className="animate-spin" />}
                {fr ? "Me désabonner" : "Unsubscribe me"}
              </button>
            </form>
          )}
        </div>
      </FadeUp>
    </div>
  );
}
